import { useCallback } from 'react';
import { validateFieldsPromise } from '../../../stream/feature/multipleFieldValidations';
import { validateFields } from '../../../stream/transfer/control';
import { isEmptyResult } from '../../../validations/utils';
import useContextOrStorage from '../../stream/useContextOrStorage';
import useReduxStore from '../useReduxStore';
import useValidateFields from '../useValidateFields';
/**
 * Returns function for fields validations which is resulting as promise.
 *
 * @documented
 * @category Form
 * @import import useValidateFieldsPromise from '@gef-ui/form-core/hooks/form/useValidateFieldsPromise'
 * @param {String} [formName] - when used, formStream$ is taken from stream storage instead of context
 * @returns {Function} [useValidateFieldsPromise] - or undefined if form is not yet registered
 * @example
 *
 * 	const validateFieldsPromise = useValidateFieldsPromise();
 *
 * 	validateFieldsPromise([['minionFirstName', 0],['minionPrice']]);
 *
 */ const useValidateFieldsPromise = (formName)=>{
    const streamContext = useContextOrStorage(formName);
    const validateFieldsSynchronize = useValidateFields(formName);
    const reduxStore = useReduxStore();
    const validateFieldsPromiseCb = useCallback((fieldsToValidate, groups, syncChanges = true)=>{
        // this may be confusing, but it seems that is most straightforward solution.
        // I suppose that RXJS will not be extended to whole application and application will remain promised base.
        // So this will run validations twice, first run is for from streams and second is for use.
        // I don't expect any race conditions and data inconsistency between runs.
        // Ofc that is possible to make it in one run, but so far this solution is OK.
        if (syncChanges) {
            validateFieldsSynchronize(fieldsToValidate, groups);
        }
        return validateFieldsPromise(validateFields({
            fieldsToValidate,
            groups
        }), streamContext, reduxStore).then((result)=>{
            if (isEmptyResult(result)) {
                return result;
            } else {
                return Promise.reject(result);
            }
        });
    }, [
        streamContext,
        validateFieldsSynchronize,
        reduxStore
    ]);
    return streamContext ? validateFieldsPromiseCb : undefined;
};
export default useValidateFieldsPromise;
